import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import useQuery from '../../../../modules/react/hooks/useQuery';
import RentalModalComponent from '../components/RentalModal';
import ReservationConfirmationModal from './ReservationConfirmationModal';
import mixpanel from '../../../../src/web/utils/mixpanel';
import { compose } from '../../../../src/web/utils/react';
import {
  submit,
  clearReservation,
} from '../../../../src/web/store/modules/reservations';


const propTypes = {
  facilityId: PropTypes.string.isRequired,
  unitGroupId: PropTypes.string.isRequired,
};

const defaultProps = {};

const GET_RENTAL_MODAL_DATA = gql`
  query GetRentalModalData($facilityId: ID!, $unitGroupId: ID!) {
    facility(id: $facilityId) {
      id
      unitGroup(id: $unitGroupId) {
        ...RentalModalUnitGroup
        ...ReservationConfirmationModalUnitGroup
      }
      ...RentalModalFacility
      ...ReservationConfirmationModalFacility
    }
    currentSite {
      id
      companyId
      identifier
    }
    omiSettings(facilityId: $facilityId) {
      id
      omiEnabled
    }
  }
  ${RentalModalComponent.fragments.facility}
  ${RentalModalComponent.fragments.unitGroup}
  ${ReservationConfirmationModal.fragments.facility}
  ${ReservationConfirmationModal.fragments.unitGroup}
`;

const getFullMoveInURL = (url, leadId) => {
  const mixpanelDistinctId = mixpanel.getDistinctId();
  const moveInURL = url.replace(/#/, `&leadId=${leadId}#`);
  const fullMoveInURL = mixpanelDistinctId
    ? `${moveInURL.replace(/companyId=/, `mpid=${mixpanelDistinctId}&companyId=`)}/`
    : `${moveInURL}/`;
  return fullMoveInURL;
};

const RentalModal = ({
  facilityId,
  facility,
  onClose,
  unitGroupId,
  discountPlans,
  handleSubmit,
  reservations,
  resetReservation,
  tier,
  ...rest
}) => {
  const autoAppliedPlans = discountPlans?.filter(plan => plan.autoApply === true);
  const discountIds = autoAppliedPlans.map(plan => plan.id);
  const [openMoveIn, setOpenMoveIn] = useState(false);
  const [reservationToBeTracked, setReservationToBeTracked] = useState(true);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [selectedDiscountPlan, setSelectedDiscountPlan] = useState(null);

  const { discountPlans: discountPlansFacility } = facility;

  const { data, error } = useQuery(GET_RENTAL_MODAL_DATA, {
    variables: { facilityId, unitGroupId },
  });

  const { submitting, receipt, error: reservationError } = reservations;

  if (!data || error) {
    return null;
  }

  const handleReservation = async (values) => {
    const { type, email, firstName, lastName, phoneNumber } = values;
    const origin = data?.omiSettings?.omiEnabled ? window?.location?.origin : null;

    const reservation = {
      unitGroupId,
      tenant: {
        phone: phoneNumber.replace(/\D/g, ''),
        email,
        firstName,
        lastName,
      },
      source: type === 'move-in' ? 'Move-In Started' : 'Reservation',
      moveInDate: new Date().toString(),
      unitTierId: tier?.tierId,
      origin,
    };
    if (discountIds) { reservation.autoApplyDiscountPlanIds = discountIds; }

    if (selectedDiscountPlan) {
      reservation.discountPlanId = selectedDiscountPlan.id;
    }

    handleSubmit({
      facilityId,
      ...reservation,
    });

    setSubmitErrors([]);

    if (type === 'move-in') {
      setOpenMoveIn(true);
    }
  };

  const closeHandler = () => {
    resetReservation();
    onClose();
  };

  const handlePromoUpdate = (selectedPlan) => {
    setSelectedDiscountPlan(selectedPlan);
  };


  if (reservations?.receipt?.userErrors && submitErrors.length === 0) {
    setSubmitErrors(reservations.receipt.userErrors);
  }

  if (submitErrors.length === 0 && receipt && !reservationError && reservationToBeTracked) {
    const { currentSite: { companyId, identifier: companyIdentifier } } = data;
    const { tenant:
      { firstName, lastName, email, phone } } = receipt;
    const tenant = { firstName, lastName, email, phone };
    mixpanel.trackReservation({ companyId, companyIdentifier }, tenant);
    setReservationToBeTracked(false);
  }

  if (reservationError && submitErrors.length === 0) {
    submitErrors.push({ code: 0, message: 'Could not reserve unit' });
  }

  if (submitErrors.length === 0 && receipt && openMoveIn) {
    const { id: leadId } = receipt;
    if (data?.omiSettings?.omiEnabled) {
      return window.open(`/move-in/${leadId}`, '_self');
    }
    const { moveInUrl } = rest;
    const fullMoveInURL = getFullMoveInURL(moveInUrl, leadId);
    window.open(fullMoveInURL, '_blank');

    setOpenMoveIn(false);
  }

  if (submitErrors.length === 0 && reservations?.receipt) {
    return (
      <ReservationConfirmationModal
        facility={data?.facility}
        onClose={closeHandler}
        reservation={{
          id: reservations?.receipt?.id,
          moveInDate: reservations?.receipt?.moveInDate,
          charges: reservations?.receipt?.invoiceableFees,
          tenant: {
            ...reservations?.receipt?.tenant,
            phoneNumber: reservations?.receipt?.tenant?.phone,
          },
          totalDue: reservations?.receipt?.dueAtMoveIn,
          reservationMoveInUrl: '', // You can fill this if needed
          unitGroupId,
          discountPlans: reservations?.receipt?.discountPlans,
        }}
        enableMoveInNow={data?.omiSettings?.omiEnabled}
        tier={tier}
      />
    );
  }

  return (
    <RentalModalComponent
      facility={data?.facility}
      onClose={closeHandler}
      submitErrors={submitErrors}
      submitReservation={handleReservation}
      onPromoUpdate={handlePromoUpdate}
      discountPlans={discountPlansFacility}
      appliedPromos={autoAppliedPlans}
      selectedDiscountPlan={selectedDiscountPlan}
      submitting={submitting}
      tier={tier}
      {...rest}
    />
  );
};

RentalModal.propTypes = propTypes;
RentalModal.defaultProps = defaultProps;

export default compose(
  connect(
    ({ reservations }, { id }) => ({
      reservations,
      id,
    }),
    {
      handleSubmit: submit,
      resetReservation: clearReservation,
    },
  ),
)(RentalModal);
